import { cap } from '../utils/format';
import React, { useState, useEffect } from 'react';
import {
  Monitor, Ticket, Package, Wifi, Phone, Mail, Key, User,
  Plus, RefreshCw, Building2, Briefcase, Hash, Globe, Clock, ShoppingBag,
  MessageSquare, Send, X, Sparkles, Bot, HelpCircle, ChevronDown, Lightbulb
} from 'lucide-react';
import api from '../api/client';
import ItemHistoryList, { itemHistoryCount } from '../components/ItemHistoryList';
import { useAuth } from '../context/AuthContext';
import { useAIFeatures } from '../hooks/useAIFeatures';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, ErrMsg } from '../components/FormField';
import WarrantyBadge from '../components/WarrantyBadge';
import { PageHeader } from '../components/FilterBar';
import PolicyStatusWidget from '../components/PolicyStatusWidget';

const CATEGORIES = ['hardware_issue','software_issue','network_issue','email_issue','access_request','printer_issue','phone_issue','other'];

// Ticket progress tracker — shows steps from raised to closed
const TICKET_STEPS = [
  { key: 'pending_review', label: 'Pending\nReview', short: 'Pending' },
  { key: 'open',           label: 'Open',            short: 'Open' },
  { key: 'in_progress',   label: 'In\nProgress',    short: 'In Progress' },
  { key: 'resolved',      label: 'Resolved',         short: 'Resolved' },
];
const STATUS_ORDER = { pending_review: 0, open: 1, in_progress: 2, resolved: 3, closed: 4 };

function TicketProgressBar({ ticket: rawTicket }) {
  // board_approval renders at the In Progress stage on the user-facing tracker
  const ticket = rawTicket.status === 'board_approval' ? { ...rawTicket, status: 'in_progress' } : rawTicket;
  if (ticket.status === 'closed') return null;
  const currentIdx = STATUS_ORDER[ticket.status] ?? 1;
  const steps = ticket.status === 'pending_review' ? TICKET_STEPS : TICKET_STEPS.filter(s => s.key !== 'pending_review');
  const activeIdx = steps.findIndex(s => s.key === ticket.status);
  return (
    <div className="mt-2.5 mb-0.5">
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {steps.map((step, i) => {
          const done = STATUS_ORDER[step.key] < currentIdx;
          const active = step.key === ticket.status;
          const color = done ? '#10b981' : active ? '#3b82f6' : '#334155';
          const bgColor = done ? 'rgba(16,185,129,0.15)' : active ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.04)';
          const borderColor = done ? '#10b981' : active ? '#3b82f6' : 'rgba(255,255,255,0.08)';
          return (
            <React.Fragment key={step.key}>
              {i > 0 && (
                <div style={{ flex: 1, height: 2, background: done || active ? (done ? '#10b981' : '#3b82f6') : 'rgba(255,255,255,0.07)', minWidth: 12 }}/>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: bgColor, border: `2px solid ${borderColor}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {done
                    ? <svg width="10" height="10" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#10b981" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    : <div style={{ width: 6, height: 6, borderRadius: '50%', background: active ? '#3b82f6' : '#334155' }}/>
                  }
                </div>
                <span style={{ fontSize: 9, color, fontWeight: active ? 700 : 500, whiteSpace: 'nowrap', letterSpacing: '0.01em' }}>
                  {step.short}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Shared elapsed-time badge (same logic as Tickets.js)
function ElapsedBadge({ ticket }) {
  const start = ticket.reported_date || ticket.created_at;
  if (!start) return null;
  const startMs = new Date(start).getTime();
  const active  = ['open','in_progress','on_hold'].includes(ticket.status);
  const endMs   = active ? Date.now() : new Date(ticket.resolved_date || ticket.closed_date || ticket.updated_at).getTime();
  const hours   = Math.max(0, (endMs - startMs) / 3600000);
  const days    = Math.floor(hours / 24);
  const remH    = Math.floor(hours % 24);
  const mins    = Math.floor((hours * 60) % 60);
  const label   = days > 0 ? `${days}d ${remH}h` : hours >= 1 ? `${Math.floor(hours)}h ${mins}m` : `${mins}m`;
  const color   = !active ? '#475569' : hours > 48 ? '#ef4444' : hours >= 8 ? '#f59e0b' : '#10b981';
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:6,
      background:`${color}18`, color, border:`1px solid ${color}35`, whiteSpace:'nowrap' }}>
      {active ? '⏱ ' : '✓ '}{label}
    </span>
  );
}

// Account state as stored on employee_systems / employee_emails
const ACCOUNT_STATUS = {
  active:           { label: 'Active',           color: '#34d399' },
  disabled:         { label: 'Disabled',         color: '#f87171' },
  password_changed: { label: 'Password changed', color: '#60a5fa' },
  forwarded:        { label: 'Forwarded',        color: '#fbbf24' },
};
function AccountStatus({ status }) {
  const s = ACCOUNT_STATUS[status] || ACCOUNT_STATUS.active;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: `${s.color}1f`, color: s.color, border: `1px solid ${s.color}40` }}>{s.label}</span>
  );
}

// Shows the password IT stored for one of the user's OWN accounts.
// Fetched only on demand from /employees/me/accounts/... (never sent with the
// profile), and hidden again automatically after 30 seconds.
function MyPasswordReveal({ type, item }) {
  const [password, setPassword] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const timer = React.useRef(null);

  React.useEffect(() => () => clearTimeout(timer.current), []);

  const hide = () => { clearTimeout(timer.current); setPassword(null); setErr(''); };
  const show = async () => {
    if (password) return hide();
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/employees/me/accounts/${type}/${item.id}/reveal-password`);
      setPassword(r.data.password);
      timer.current = setTimeout(() => setPassword(null), 30000);   // auto-hide
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not show the password');
    } finally { setBusy(false); }
  };

  if (!item.password_set) return null;
  return (
    <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
      <span style={{ color: '#475569' }}>🔑 Password:</span>
      {password
        ? <code className="font-mono px-1.5 py-0.5 rounded"
                style={{ color: '#fbbf24', background: 'rgba(251,191,36,0.1)', userSelect: 'all' }}>{password}</code>
        : <span style={{ color: '#475569' }}>••••••••</span>}
      <button type="button" onClick={show} disabled={busy} className="underline"
        style={{ color: '#60a5fa', cursor: busy ? 'wait' : 'pointer' }}
        title={password ? 'Hide it again' : 'Show the password IT set for this account'}>
        {busy ? 'Please wait…' : password ? 'Hide' : 'Show'}
      </button>
      {password && <span style={{ color: '#475569' }}>hides itself in 30s</span>}
      {err && <span style={{ color: '#f87171' }}>{err}</span>}
    </div>
  );
}

function Section({ icon: Icon, color, title, count, children }) {
  return (
    <div className="glass glow-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
          <Icon size={14} style={{ color }}/>
        </div>
        <span className="font-semibold text-sm" style={{ color: '#94a3b8' }}>{title}</span>
        {count !== undefined && (
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: '#475569' }}>{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ msg }) {
  return <div className="py-6 text-center text-sm" style={{ color: '#1e3a5f' }}>{msg}</div>;
}

// Equipment health line — age + expected replacement window for an asset
function AssetHealthLine({ asset }) {
  if (!asset.purchased_date) return null;
  const now = new Date();
  const bought = new Date(asset.purchased_date);
  const months = Math.max(0, (now.getFullYear() - bought.getFullYear()) * 12 + now.getMonth() - bought.getMonth());
  const ageLabel = months >= 12 ? `${Math.floor(months / 12)}y ${months % 12}m` : `${months}m`;
  const lifeEnd = asset.depreciation_end_date ? new Date(asset.depreciation_end_date) : null;
  let replLabel = null, replColor = '#475569';
  if (lifeEnd) {
    const monthsLeft = (lifeEnd.getFullYear() - now.getFullYear()) * 12 + lifeEnd.getMonth() - now.getMonth();
    if (monthsLeft <= 0) { replLabel = 'Replacement window reached'; replColor = '#f59e0b'; }
    else if (monthsLeft <= 6) { replLabel = `Replacement due in ~${monthsLeft}m`; replColor = '#f59e0b'; }
    else { replLabel = `Replacement due ${lifeEnd.getFullYear()}`; replColor = '#475569'; }
  }
  return (
    <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
      Age: <span style={{ color: '#94a3b8' }}>{ageLabel}</span>
      {replLabel && <> · <span style={{ color: replColor }}>{replLabel}</span></>}
    </div>
  );
}

// Similar past tickets — free "AI-style" suggestions using backend trigram search
function SimilarTickets({ query, onUseCategory }) {
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!query || query.trim().length < 8) { setResult(null); return; }
    const t = setTimeout(() => {
      api.get('/tickets/similar', { params: { q: query.trim() } })
        .then(r => setResult(r.data)).catch(() => setResult(null));
    }, 600);
    return () => clearTimeout(t);
  }, [query]);
  if (!result?.matches?.length) return null;
  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb size={13} style={{ color: '#34d399' }}/>
        <span className="text-xs font-semibold" style={{ color: '#34d399' }}>Similar issues were resolved before — this might help:</span>
      </div>
      <div className="space-y-2">
        {result.matches.filter(m => m.resolution).slice(0, 3).map((m, i) => (
          <div key={i} className="text-xs px-2.5 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <div className="font-medium" style={{ color: '#e2e8f0' }}>{m.title}</div>
            <div className="mt-1" style={{ color: '#94a3b8' }}>{m.resolution}</div>
          </div>
        ))}
      </div>
      {result.suggested_category && onUseCategory && (
        <button type="button" onClick={() => onUseCategory(result.suggested_category)}
          className="mt-2 text-xs px-2.5 py-1 rounded-lg" style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
          Suggested category: {result.suggested_category.replace(/_/g, ' ')} — click to apply
        </button>
      )}
    </div>
  );
}

const ACCESSORY_ITEMS = [
  'Mobile Tempered Glass','Mobile Back Cover','Laptop Power Adapter','Laptop Power Cable',
  'Mobile Phone Charger Cable','Mobile Phone Power Adapter','Wired Mouse','Wireless Mouse','USB Drive'
];
const STATUS_COLORS = { submitted:'#f59e0b', pending_board_approval:'#818cf8', approved:'#34d399', rejected_by_it:'#ef4444', rejected_by_board:'#ef4444', completed:'#10b981' };
const STATUS_LABELS = { submitted:'Submitted — Awaiting IT', pending_board_approval:'Pending Board Approval', approved:'Approved', rejected_by_it:'Rejected by IT', rejected_by_board:'Rejected by Board', completed:'Completed' };

export default function MyPortal() {
  const { user, isGeneralUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [assets, setAssets] = useState([]);
  const [itemHistory, setItemHistory] = useState({ assets: [], accessories: [] });
  const [accessories, setAccessories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [dongles, setDongles] = useState([]);
  const [voice, setVoice] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [accRequests, setAccRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const aiFeatures = useAIFeatures();
  // AI Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([{ role:'assistant', content:'Hi! I\'m your SynergyNode IT Assistant. Ask me anything about IT support, your assets, or how to raise a ticket.' }]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [accReqForm, setAccReqForm] = useState({ items: [], other: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categoryEta, setCategoryEta] = useState({});
  const [kb, setKb] = useState({});
  const [kbOpen, setKbOpen] = useState(null);
  const [catalog, setCatalog] = useState([]);

  // Loose match of a request item label against available stock names
  const stockFor = (label) => {
    const l = label.toLowerCase();
    const hit = catalog.find(c => c.key.includes(l) || l.includes(c.key));
    return hit ? hit.available : null;
  };

  const load = async () => {
    setLoading(true);
    try {
      // For non-general roles (director/finance/junior_it), scope to personal data via employee_id
      const assetParams = isGeneralUser ? { limit: 100 } : { limit: 100, holder_id: user.employee_id || undefined };
      const ticketParams = isGeneralUser ? { limit: 50 } : { limit: 50, employee_id: user.employee_id || undefined };
      const calls = [
        api.get('/assets', { params: assetParams }).then(r => r.data.data || []).catch(() => []),
        api.get('/accessories', { params: { limit: 100 } }).then(r => r.data.data || []).catch(() => []),
        api.get('/tickets', { params: ticketParams }).then(r => r.data.tickets || r.data.data || []).catch(() => []),
        api.get('/tickets/my-stats').then(r => r.data).catch(() => null),
        api.get('/accessory-requests').then(r => r.data.data || []).catch(() => []),
      ];
      if (user.employee_id) {
        calls.push(
          api.get('/connections/dongles', { params: { holder_id: user.employee_id, limit: 50 } }).then(r => r.data.data || []).catch(() => []),
          api.get('/connections/voice', { params: { holder_id: user.employee_id, limit: 50 } }).then(r => r.data.data || []).catch(() => []),
          api.get(`/employees/${user.employee_id}`).then(r => r.data).catch(() => null)
        );
      }
      const results = await Promise.all(calls);
      setAssets(results[0]);
      setAccessories(results[1]);
      setTickets(results[2]);
      setMyStats(results[3]);
      setAccRequests(results[4]);
      if (user.employee_id) {
        setDongles(results[5] || []);
        setVoice(results[6] || []);
        setProfile(results[7]);
      }
    } catch (e) {
      console.error('Portal load error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Lightweight extras — ETA per category, knowledge base, accessory stock catalog
  useEffect(() => {
    api.get('/tickets/category-eta').then(r => setCategoryEta(r.data || {})).catch(() => {});
    // Everything I have ever held, including items already returned or disposed
    if (user?.employee_id) api.get(`/employees/${user.employee_id}/item-history`).then(r => setItemHistory(r.data || { assets: [], accessories: [] })).catch(() => {});
    api.get('/tickets/kb').then(r => setKb(r.data || {})).catch(() => {});
    api.get('/accessory-requests/meta/catalog').then(r => setCatalog(r.data || [])).catch(() => {});
  }, []);

  const submitAccRequest = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (!accReqForm.items.length && !accReqForm.other?.trim()) { setError('Select at least one item or describe in Other.'); setSaving(false); return; }
      if (!accReqForm.reason?.trim()) { setError('Reason is required.'); setSaving(false); return; }
      await api.post('/accessory-requests', {
        requested_items: accReqForm.items,
        other_item: accReqForm.other?.trim() || undefined,
        reason: accReqForm.reason
      });
      setModal(null); setAccReqForm({ items: [], other: '', reason: '' }); load();
    } catch (err) { setError(err.response?.data?.error || 'Submission failed'); } finally { setSaving(false); }
  };

  const submitTicket = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      // Handle acc: prefix for accessories (add note to description, clear asset ID)
      let assetId = form.related_asset_id;
      let extraDesc = '';
      if (assetId?.startsWith('acc:')) {
        const accId = assetId.replace('acc:', '');
        const acc = accessories.find(a => a.id === accId);
        if (acc) {
          extraDesc = `\n\n[Affected Accessory: ${acc.name || `${acc.brand} ${acc.model}`}${acc.serial_number ? ` · S/N: ${acc.serial_number}` : ''}]`;
        }
        assetId = undefined;
      }
      await api.post('/tickets', {
        title: form.title,
        description: (form.description || '') + extraDesc,
        category: form.category || 'hardware_issue',
        priority: form.priority || 'medium',
        status: 'open',
        reported_by_employee_id: user.employee_id,
        related_asset_id: assetId || undefined,
      });
      setModal(null); setForm({}); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to submit'); } finally { setSaving(false); }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = { role: 'user', content: chatInput.trim() };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    try {
      const r = await api.post('/ai/chat', {
        messages: [...chatMessages, userMsg],
        context: {
          assets: assets.slice(0, 5).map(a => ({ asset_number: a.asset_number, category: a.category, brand: a.brand, model: a.model })),
          recentTickets: tickets.slice(0, 3).map(t => ({ status: t.status, title: t.title })),
          policies: []
        }
      });
      const assistantMsg = { role: 'assistant', content: r.data.response, suggestedTitle: r.data.suggestedTitle };
      setChatMessages(prev => [...prev, assistantMsg]);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, AI is not available right now. Please raise a ticket if you need IT support.' }]);
    } finally { setChatLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 gap-3" style={{ color: '#334155' }}>
      <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
      Loading your profile...
    </div>
  );

  const emp = profile;

  return (
    <div className="space-y-5">

      {/* Policy status widget */}
      <PolicyStatusWidget />

      {/* Profile header */}
      <div className="glass glow-border rounded-2xl p-6" style={{ background: 'linear-gradient(135deg,rgba(59,130,246,0.1),rgba(99,102,241,0.05))' }}>
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,rgba(59,130,246,0.2),rgba(99,102,241,0.2))', border: '1px solid rgba(99,102,241,0.3)' }}>
            <User size={28} style={{ color: '#818cf8' }}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>{emp?.full_name || user?.full_name}</div>
            {emp?.designation && <div className="text-sm mt-0.5" style={{ color: '#60a5fa' }}>{emp.designation}</div>}
            <div className="flex flex-wrap gap-3 mt-3 text-xs" style={{ color: '#475569' }}>
              {emp?.employee_code && (
                <span className="flex items-center gap-1"><Hash size={11}/>{emp.employee_code}</span>
              )}
              {emp?.department?.name && (
                <span className="flex items-center gap-1"><Briefcase size={11}/>{emp.department.name}</span>
              )}
              {emp?.company?.name && (
                <span className="flex items-center gap-1"><Building2 size={11}/>{emp.company.name}</span>
              )}
              {emp?.email && (
                <span className="flex items-center gap-1"><Mail size={11}/>{emp.email}</span>
              )}
              {emp?.phone && (
                <span className="flex items-center gap-1"><Phone size={11}/>{emp.phone}</span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
            <button onClick={() => { setForm({ priority: 'medium', category: 'hardware_issue' }); setError(''); setModal('ticket'); }}
              className="btn-primary text-xs whitespace-nowrap"><Plus size={12}/> Raise Ticket</button>
          </div>
        </div>
        {/* Quick stat pills */}
        <div className="flex gap-3 mt-4 flex-wrap">
          {[
            { label: 'Assets', val: assets.length, color: '#3b82f6' },
            { label: 'Accessories', val: accessories.length, color: '#8b5cf6' },
            { label: 'Tickets', val: tickets.length, color: '#f59e0b' },
            { label: 'Dongles', val: dongles.length, color: '#10b981' },
            { label: 'Voice Numbers', val: voice.length, color: '#06b6d4' },
          ].map((s, i) => (
            <div key={i} className="px-3 py-1.5 rounded-xl text-xs font-medium"
              style={{ background: `${s.color}15`, border: `1px solid ${s.color}25`, color: s.color }}>
              {s.val} {s.label}
            </div>
          ))}
        </div>

        {/* SynergyNode Portal Credentials */}
        <div className="mt-4 rounded-xl p-3" style={{ background: 'rgba(29,78,216,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
          <div className="text-xs font-semibold mb-2" style={{ color: '#60a5fa' }}>Your SynergyNode Login</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex gap-2">
              <span style={{ color: '#334155', minWidth: 70 }}>Username</span>
              <span className="font-mono font-semibold" style={{ color: '#e2e8f0' }}>{user?.username || '—'}</span>
            </div>
            <div className="flex gap-2">
              <span style={{ color: '#334155', minWidth: 70 }}>System URL</span>
              <a href={window.location.origin} style={{ color: '#60a5fa' }}>{window.location.hostname}</a>
            </div>
            <div className="flex gap-2 col-span-2">
              <span style={{ color: '#334155', minWidth: 70 }}>Password</span>
              <span style={{ color: '#475569' }}>••••••••  <span style={{ color: '#334155' }}>(use Change Password in sidebar to update)</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Assets */}
      <Section icon={Monitor} color="#3b82f6" title="Assigned Assets" count={assets.length}>
        {assets.length === 0 ? <EmptyState msg="No assets currently assigned to you"/> : (
          <div className="space-y-2">
            {assets.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <Monitor size={13} style={{ color: '#60a5fa' }}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold" style={{ color: '#60a5fa' }}>{a.asset_number}</span>
                    <span className="text-xs" style={{ color: '#475569' }}>{a.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </div>
                  <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{a.brand} {a.model}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
                    {a.serial_number ? `S/N: ${a.serial_number}` : ''}
                    {a.allocated_date ? ` · Allocated: ${a.allocated_date}` : a.purchased_date ? ` · Purchased: ${a.purchased_date}` : ''}
                  </div>
                  <AssetHealthLine asset={a}/>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge value={a.status}/>
                  {a.warranty_period && a.warranty_period !== 'none' && (
                    <WarrantyBadge warrantyPeriod={a.warranty_period} purchasedDate={a.purchased_date} small/>
                  )}
                  <button onClick={() => window.open(`/api/assets/${a.id}/handover-receipt?token=${localStorage.getItem('token')}`, '_blank')}
                    className="text-xs px-2 py-0.5 rounded-lg" title="Print handover receipt"
                    style={{ background: 'rgba(59,130,246,0.08)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)' }}>
                    🖨 Receipt
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Accessories */}
      {(accessories.length > 0) && (
        <Section icon={Package} color="#8b5cf6" title="Assigned Accessories" count={accessories.length}>
          <div className="space-y-2">
            {accessories.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{a.name || `${a.brand} ${a.model}`}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
                    {a.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}{a.serial_number ? ` · S/N: ${a.serial_number}` : ''}
                  </div>
                </div>
                <Badge value={a.status}/>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Everything I have held — current and past, with the period used */}
      <Section icon={Monitor} color="#14b8a6" title="My Asset & Accessory History" count={itemHistoryCount(itemHistory)}>
        <ItemHistoryList history={itemHistory}
          emptyMsg="Nothing has been allocated to you yet"/>
      </Section>

      {/* System Logins */}
      {emp?.systems?.length > 0 && (
        <Section icon={Key} color="#f59e0b" title="System Logins" count={emp.systems.length}>
          <div className="space-y-2">
            {emp.systems.map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{s.system_name}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
                    Username: <span className="font-mono" style={{ color: '#94a3b8' }}>{s.username || '—'}</span>
                    {s.access_level ? ` · Level: ${s.access_level}` : ''}
                  </div>
                  {s.system_url && (
                    <div className="text-xs mt-0.5">
                      <a href={s.system_url} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>{s.system_url}</a>
                    </div>
                  )}
                  <MyPasswordReveal type="system" item={s}/>
                </div>
                <AccountStatus status={s.status}/>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Email Accounts */}
      {emp?.emails?.length > 0 && (
        <Section icon={Mail} color="#06b6d4" title="Email Accounts" count={emp.emails.length}>
          <div className="space-y-2">
            {emp.emails.map((e, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Mail size={14} style={{ color: '#06b6d4', flexShrink: 0 }}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{e.email_address}</div>
                  {e.email_type && <div className="text-xs mt-0.5" style={{ color: '#475569' }}>{e.email_type}</div>}
                  <MyPasswordReveal type="email" item={e}/>
                </div>
                <AccountStatus status={e.status}/>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Voice Numbers */}
      {voice.length > 0 && (
        <Section icon={Phone} color="#10b981" title="Voice Numbers" count={voice.length}>
          <div className="space-y-2">
            {voice.map((v, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium font-mono" style={{ color: '#e2e8f0' }}>{v.number}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
                    {v.isp_name || '—'}{v.package_name ? ` · ${v.package_name}` : ''}{v.company_name ? ` · ${v.company_name}` : ''}
                  </div>
                </div>
                <Badge value={v.status}/>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Dongles */}
      {dongles.length > 0 && (
        <Section icon={Wifi} color="#06b6d4" title="Dongles" count={dongles.length}>
          <div className="space-y-2">
            {dongles.map((d, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium font-mono" style={{ color: '#e2e8f0' }}>{d.number}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
                    {d.isp_name || '—'}{d.package_name ? ` · ${d.package_name}` : ''}{d.account_number ? ` · Acc: ${d.account_number}` : ''}
                  </div>
                </div>
                <Badge value={d.status}/>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* My Tickets */}
      <Section icon={Ticket} color="#f59e0b" title="My IT Tickets" count={tickets.length}>
        <div className="flex justify-end mb-3">
          <button onClick={() => { setForm({ priority: 'medium', category: 'hardware_issue' }); setError(''); setModal('ticket'); }}
            className="btn-primary text-xs"><Plus size={12}/> Raise Ticket</button>
        </div>
        {tickets.length === 0 ? (
          <EmptyState msg="No tickets yet. Click 'Raise Ticket' if you have an IT issue."/>
        ) : (() => {
          const active = tickets.filter(t => t.status !== 'closed');
          const closed = tickets.filter(t => t.status === 'closed');
          return (
            <div className="space-y-3">
              {active.map((t, i) => (
                <div key={i} className="p-3 rounded-xl" style={{ background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.15)' }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold" style={{ color: '#60a5fa' }}>{t.ticket_number}</span>
                        <Badge value={t.priority}/>
                        <Badge value={t.status}/>
                      </div>
                      <div className="text-sm font-medium mt-1" style={{ color: '#e2e8f0' }}>{t.title}</div>
                      <div className="text-xs mt-0.5" style={{ color: '#475569' }}>
                        {t.category?.replace(/_/g, ' ')} · {t.reported_date ? new Date(t.reported_date).toLocaleDateString('en-GB') : '—'}
                      </div>
                    </div>
                    <div className="flex-shrink-0"><ElapsedBadge ticket={t}/></div>
                  </div>
                  <TicketProgressBar ticket={t}/>
                </div>
              ))}
              {closed.length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-2 mt-1" style={{ color: '#475569' }}>Closed Tickets ({closed.length})</div>
                  <div className="space-y-2">
                    {closed.map((t, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs" style={{ color: '#475569' }}>{t.ticket_number}</span>
                            <Badge value={t.priority}/>
                          </div>
                          <div className="text-xs mt-0.5 truncate" style={{ color: '#64748b' }}>{t.title}</div>
                        </div>
                        <Badge value="closed"/>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </Section>

      {/* IT Response Time */}
      {myStats && (
        <Section icon={Clock} color="#06b6d4" title="My IT Support Stats">
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total Tickets', val: myStats.total || 0, color: '#818cf8' },
              { label: 'Open', val: myStats.open || 0, color: '#f59e0b' },
              { label: 'Resolved', val: myStats.resolved || 0, color: '#34d399' },
              { label: 'Avg Response', val: myStats.avg_hours != null ? `${myStats.avg_hours}h` : '—', color: '#06b6d4' },
            ].map((s, i) => (
              <div key={i} className="rounded-xl p-3 text-center" style={{ background: `${s.color}10`, border: `1px solid ${s.color}20` }}>
                <div className="text-xl font-bold" style={{ color: s.color }}>{s.val}</div>
                <div className="text-xs mt-0.5" style={{ color: '#475569' }}>{s.label}</div>
              </div>
            ))}
          </div>
          {myStats.avg_hours != null && (
            <div className="text-xs mt-3 px-1" style={{ color: '#334155' }}>
              Average IT response time based on your ticket history (time from raising to resolution).
            </div>
          )}
        </Section>
      )}

      {/* Help Center — self-service answers from past resolved tickets */}
      {Object.keys(kb).length > 0 && (
        <Section icon={HelpCircle} color="#34d399" title="Help Center — Common Fixes" count={Object.values(kb).reduce((n, arr) => n + arr.length, 0)}>
          <div className="text-xs mb-3" style={{ color: '#475569' }}>
            Answers from previously resolved IT issues. Check here before raising a ticket — your problem may have a quick fix.
          </div>
          <div className="space-y-2">
            {Object.entries(kb).map(([cat, items]) => (
              <div key={cat} className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                <button onClick={() => setKbOpen(kbOpen === cat ? null : cat)}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left"
                  style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <span className="text-sm font-medium capitalize" style={{ color: '#e2e8f0' }}>{cat.replace(/_/g, ' ')}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(52,211,153,0.1)', color: '#34d399' }}>{items.length}</span>
                  <ChevronDown size={14} className="ml-auto" style={{ color: '#475569', transform: kbOpen === cat ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}/>
                </button>
                {kbOpen === cat && (
                  <div className="px-4 py-3 space-y-3" style={{ background: 'rgba(0,0,0,0.15)' }}>
                    {items.map((it, i) => (
                      <div key={i}>
                        <div className="text-xs font-semibold" style={{ color: '#94a3b8' }}>{it.title}</div>
                        <div className="text-xs mt-1 whitespace-pre-wrap" style={{ color: '#64748b' }}>{it.resolution}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* IT Accessory Requests */}
      <Section icon={ShoppingBag} color="#f59e0b" title="IT Accessory Requests" count={accRequests.length}>
        <div className="flex justify-end mb-3">
          <button onClick={() => { setAccReqForm({ items: [], other: '', reason: '' }); setError(''); setModal('accReq'); }}
            className="btn-primary text-xs"><Plus size={12}/> New Request</button>
        </div>
        {accRequests.length === 0 ? <EmptyState msg="No accessory requests submitted yet"/> : (
          <div className="space-y-2">
            {accRequests.map((r, i) => {
              const color = STATUS_COLORS[r.status] || '#475569';
              const items = [...(r.requested_items || []), r.other_item].filter(Boolean).join(', ');
              return (
                <div key={i} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-xs font-medium" style={{ color: '#e2e8f0' }}>{items || '—'}</div>
                      <div className="text-xs mt-0.5" style={{ color: '#475569' }}>{r.reason}</div>
                      {r.it_notes && <div className="text-xs mt-1 italic" style={{ color: '#60a5fa' }}>IT: {r.it_notes}</div>}
                      {r.rejection_reason && <div className="text-xs mt-1 italic" style={{ color: '#f87171' }}>Reason: {r.rejection_reason}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                      <div className="text-xs mt-1" style={{ color: '#334155' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : ''}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Accessory Request Modal */}
      {/* ── AI Chat Widget (Feature 2) ── */}
      {/* Floating button — only shown when feature is enabled */}
      {aiFeatures.chat && <button onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl"
        style={{ background: chatOpen ? '#7c3aed' : 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: '2px solid rgba(139,92,246,0.5)' }}
        title="IT Assistant">
        {chatOpen ? <X size={20} style={{ color:'#fff' }}/> : <Bot size={22} style={{ color:'#fff' }}/>}
      </button>}

      {/* Chat panel */}
      {aiFeatures.chat && chatOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-96 rounded-2xl overflow-hidden flex flex-col shadow-2xl"
          style={{ height: 480, background: 'linear-gradient(160deg,#0d1528,#090e1c)', border: '1px solid rgba(139,92,246,0.35)' }}>
          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-2.5 flex-shrink-0"
            style={{ background:'rgba(139,92,246,0.15)', borderBottom:'1px solid rgba(139,92,246,0.2)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background:'rgba(139,92,246,0.3)' }}>
              <Sparkles size={14} style={{ color:'#c084fc' }}/>
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color:'#e2e8f0' }}>IT Assistant</div>
              <div className="text-xs" style={{ color:'#7c3aed' }}>SynergyNode AI · Always available</div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {chatMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${
                  m.role === 'user'
                    ? 'rounded-tr-sm'
                    : 'rounded-tl-sm'
                }`} style={{
                  background: m.role === 'user' ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.06)',
                  color: '#e2e8f0',
                  border: m.role === 'user' ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)'
                }}>
                  {m.content}
                  {m.suggestedTitle && (
                    <button
                      onClick={() => { setForm({ priority:'medium', category:'hardware_issue', title: m.suggestedTitle }); setError(''); setModal('ticket'); setChatOpen(false); }}
                      className="mt-2 w-full text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5"
                      style={{ background:'rgba(59,130,246,0.2)', color:'#60a5fa', border:'1px solid rgba(59,130,246,0.3)' }}>
                      <Ticket size={10}/> Raise ticket: "{m.suggestedTitle}"
                    </button>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl rounded-tl-sm text-xs"
                  style={{ background:'rgba(255,255,255,0.06)', color:'#475569', border:'1px solid rgba(255,255,255,0.08)' }}>
                  <span className="animate-pulse">AI is thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 flex-shrink-0" style={{ borderTop:'1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !chatLoading && sendChat()}
                placeholder="Ask about IT support, your assets..."
                className="input-dark flex-1 text-xs"
                style={{ borderColor:'rgba(139,92,246,0.3)', padding:'8px 12px' }}
              />
              <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()}
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background:'rgba(139,92,246,0.3)', border:'1px solid rgba(139,92,246,0.4)', opacity: chatLoading ? 0.5 : 1 }}>
                <Send size={13} style={{ color:'#a78bfa' }}/>
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === 'accReq' && (
        <Modal title="Request IT Accessories" onClose={() => { setModal(null); setAccReqForm({ items: [], other: '', reason: '' }); }} size="lg">
          <form onSubmit={submitAccRequest} className="space-y-4">
            <ErrMsg msg={error}/>
            <div>
              <label className="block text-xs font-semibold mb-3" style={{ color: '#94a3b8' }}>1. Select Required Accessories *</label>
              <div className="space-y-2">
                {ACCESSORY_ITEMS.map(item => (
                  <label key={item} className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all"
                    style={{ background: accReqForm.items.includes(item) ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.03)',
                             border: `1px solid ${accReqForm.items.includes(item) ? 'rgba(59,130,246,0.35)' : 'rgba(255,255,255,0.06)'}` }}>
                    <input type="checkbox" checked={accReqForm.items.includes(item)}
                      onChange={e => setAccReqForm(prev => ({
                        ...prev,
                        items: e.target.checked ? [...prev.items, item] : prev.items.filter(i => i !== item)
                      }))} className="accent-blue-500 w-4 h-4"/>
                    <span className="text-sm" style={{ color: '#e2e8f0' }}>{item}</span>
                    {stockFor(item) !== null && (
                      <span className="ml-auto text-xs px-2 py-0.5 rounded-full"
                        style={{ background: stockFor(item) > 0 ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
                                 color: stockFor(item) > 0 ? '#34d399' : '#fbbf24',
                                 border: `1px solid ${stockFor(item) > 0 ? 'rgba(16,185,129,0.25)' : 'rgba(245,158,11,0.25)'}` }}>
                        {stockFor(item) > 0 ? `${stockFor(item)} in stock` : 'To be ordered'}
                      </span>
                    )}
                  </label>
                ))}
                <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <input type="checkbox" checked={!!accReqForm.showOther}
                    onChange={e => setAccReqForm(prev => ({...prev, showOther: e.target.checked, other: e.target.checked ? prev.other : ''}))}
                    className="accent-blue-500 w-4 h-4"/>
                  <span className="text-sm" style={{ color: '#e2e8f0' }}>Other</span>
                  {accReqForm.showOther && (
                    <input type="text" value={accReqForm.other} onChange={e => setAccReqForm(prev => ({...prev, other: e.target.value}))}
                      placeholder="Describe the item" className="input-dark flex-1 text-xs py-1" style={{ marginLeft: 8 }}/>
                  )}
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-2" style={{ color: '#94a3b8' }}>2. Reason for Request *</label>
              <textarea value={accReqForm.reason} onChange={e => setAccReqForm(prev => ({...prev, reason: e.target.value}))}
                rows={3} required className="input-dark" placeholder="Explain why you need this / any other remarks..."/>
            </div>
            <div className="px-4 py-3 rounded-xl text-xs" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#fbbf24' }}>
              Please note there is a waiting time of 1-2 weeks and all requests are subject to prior approval.
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setModal(null); setAccReqForm({ items: [], other: '', reason: '' }); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving ? 'Submitting...' : 'Submit Request'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Raise Ticket Modal */}
      {modal === 'ticket' && (
        <Modal title="Raise IT Support Ticket" onClose={() => { setModal(null); setForm({}); }} size="lg">
          <form onSubmit={submitTicket} className="space-y-4">
            <ErrMsg msg={error}/>
            <FF label="Issue Title *" name="title" form={form} setForm={setForm} required placeholder="Briefly describe the issue"/>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Category</label>
                <select value={form.category||'hardware_issue'} onChange={e=>setForm({...form,category:e.target.value})} className="input-dark">
                  {CATEGORIES.map(c=><option key={c} value={c}>{c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Priority</label>
                <select value={form.priority||'medium'} onChange={e=>setForm({...form,priority:e.target.value})} className="input-dark">
                  {['low','medium','high','critical'].map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            {/* Asset or Accessory selector — only user's own items */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>
                Affected Asset / Accessory <span style={{ color: '#334155' }}>(optional)</span>
              </label>
              <select
                value={form.related_asset_id || ''}
                onChange={e => setForm({ ...form, related_asset_id: e.target.value || undefined })}
                className="input-dark">
                <option value="">— Select if issue is related to a specific item —</option>
                {assets.length > 0 && (
                  <optgroup label="My Assets">
                    {assets.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.asset_number} — {a.brand} {a.model}
                      </option>
                    ))}
                  </optgroup>
                )}
                {accessories.length > 0 && (
                  <optgroup label="My Accessories">
                    {accessories.map(a => (
                      <option key={a.id} value={`acc:${a.id}`}>
                        {a.name || `${a.brand} ${a.model}`} {a.serial_number ? `(S/N: ${a.serial_number})` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <FF label="Description *" name="description" form={form} setForm={setForm} rows={4} required
              placeholder="Describe the issue in detail — what happened, what you expected, any error messages..."/>
            <SimilarTickets query={`${form.title || ''} ${form.description || ''}`} onUseCategory={cat => setForm(f => ({ ...f, category: cat }))}/>
            <div className="px-4 py-3 rounded-xl text-xs" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa' }}>
              The IT team will be notified immediately. You will receive updates by email.
              {categoryEta[form.category || 'hardware_issue'] && (
                <div className="mt-1" style={{ color: '#94a3b8' }}>
                  ⏱ Tickets in this category are typically resolved in ~{categoryEta[form.category || 'hardware_issue'].avg_hours < 1 ? 'under 1 hour' : `${Math.round(categoryEta[form.category || 'hardware_issue'].avg_hours)} hour${Math.round(categoryEta[form.category || 'hardware_issue'].avg_hours) > 1 ? 's' : ''}`} (based on recent history).
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setModal(null); setForm({}); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving ? 'Submitting...' : 'Submit Ticket'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
